// client/src/writer/hooks/useOptimizedSave.ts
// Optimized save hooks for each tab - sends only relevant data

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../lib/queryClient';
import { useToast } from '../../hooks/use-toast';
import { cacheManager } from '../../utils/cacheUtils';
import { consolidateBackMatterAuthorInfo } from '../utils/authorUtils';

export const useOptimizedSave = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const invalidateProjectAndBlueprint = (projectId: string) => {
    queryClient.invalidateQueries({ queryKey: ['story-project-summary', projectId] });
    queryClient.invalidateQueries({ queryKey: ['project-blueprint', projectId] });
    queryClient.invalidateQueries({ queryKey: ["/api/story-projects/summaries"] }); // Summaries list for dashboard/my-stories
  };

  // Save idea hub data (idea, work type, target word count)
  const saveIdeaHub = useMutation({
    mutationFn: async ({ projectId, idea, workType, targetWordCount, setBlueprintSaving }: {
      projectId: string;
      idea?: string;
      workType?: string;
      targetWordCount?: number;
      setBlueprintSaving?: (saving: boolean) => void;
    }) => {
      // Set saving state immediately when save starts
      if (setBlueprintSaving) {
        setBlueprintSaving(true);
      }
      
      const payload: any = {};
      if (idea !== undefined) payload.idea = idea;
      if (workType !== undefined) payload.workType = workType;
      if (targetWordCount !== undefined) payload.targetWordCount = targetWordCount;

      const response = await apiRequest(`/api/story-projects/${projectId}/idea-hub`, 'PUT', payload);
      return response.json();
    },
    onSuccess: (data, variables) => {
      console.log('💾 Idea-hub save success:', {
        projectId: variables.projectId,
        hasBlueprint: !!data.blueprint,
        responseKeys: Object.keys(data),
        targetWordCount: data.targetWordCount,
        workType: data.workType,
        idea: data.idea?.substring(0, 50) + '...'
      });
      
      // Reset blueprint states immediately after successful save
      if (variables.setBlueprintSaving) {
        variables.setBlueprintSaving(false);
      }
      
      // Update localStorage cache with merged data
      if (data.blueprint) {
        cacheManager.updateBlueprintWithIdeaHub(variables.projectId, data);
      } else {
      }
      
      // CRITICAL: Update React Query cache directly with fresh API data
      const updatedData = {
        ...data, // Use all fresh data from API response
        updatedAt: new Date().toISOString()
      };
      
      queryClient.setQueryData(['story-project-summary', variables.projectId], (oldData: any) => {
        console.log('🔄 Updating React Query cache with fresh API data:', {
          oldTargetWordCount: oldData?.targetWordCount,
          newTargetWordCount: updatedData.targetWordCount,
          oldBookType: oldData?.bookType,
          newBookType: updatedData.bookType || updatedData.workType
        });
        return {
          ...oldData,
          ...updatedData, // Merge all fresh API data
        };
      });
      
      // CRITICAL: Update localStorage cache with synchronized update (all 3 cache objects)
      cacheManager.updateAllProjectCaches(variables.projectId, updatedData, 'idea-hub');
      
      // 🚨 ISSUE FIX: Don't invalidate immediately after updating cache
      // Instead, only invalidate dashboard summaries list to refresh other views
      queryClient.invalidateQueries({ queryKey: ["/api/story-projects/summaries"] });
      
      // 🔄 REFRESH: Invalidate the project summary to trigger a re-fetch and update projectState
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['story-project-summary', variables.projectId] });
      }, 100); // Small delay to ensure cache update is complete

      toast({
        title: "Project Updated",
        description: "Your idea and basic details have been saved successfully.",
      });
    },
    onError: (error: any, variables) => {
      // Reset saving state on error
      if (variables.setBlueprintSaving) {
        variables.setBlueprintSaving(false);
      }
      
      toast({
        title: "Save Failed",
        description: error.message || "Failed to save project changes.",
        variant: "destructive",
      });
    },
  });

  // Save book metadata
  const saveBookMetadata = useMutation({
    mutationFn: async ({ projectId, bookMetadata, setBlueprintSaving, onStateUpdate }: {
      projectId: string;
      bookMetadata: any;
      setBlueprintSaving?: (saving: boolean) => void;
      onStateUpdate?: (updatedData: any) => void;
    }) => {

      // Set saving state immediately when save starts
      if (setBlueprintSaving) {
        setBlueprintSaving(true);
      }

      try {
        const response = await apiRequest(`/api/story-projects/${projectId}/book-metadata`, 'PUT', { bookMetadata });
        
        const data = await response.json();
        
        return data;
      } catch (error) {
        console.error('💥 MUTATION ERROR:', error);
        throw error;
      }
    },
    onSuccess: (data, variables) => {
      
      // Reset saving state immediately after successful save
      if (variables.setBlueprintSaving) {
        variables.setBlueprintSaving(false);
      }
      
      // CRITICAL: Update React Query cache directly with fresh API data
      queryClient.setQueryData(['story-project-summary', variables.projectId], (oldData: any) => {
        console.log('🔄 Updating React Query cache with fresh book metadata API response:', {
          oldBookMetadata: oldData?.bookMetadata,
          newBookMetadata: data.bookMetadata,
          fullApiResponse: data
        });
        return {
          ...oldData,
          ...data, // Merge all fresh API data including id, uuid, updatedAt
          updatedAt: data.updatedAt || new Date().toISOString()
        };
      });
      
      // CRITICAL: Update localStorage cache with synchronized update (all 3 cache objects)
      cacheManager.updateAllProjectCaches(variables.projectId, data, 'book-metadata');

      // CRITICAL FIX: Update blueprint React Query cache used by STEP-7 hydration
      queryClient.setQueryData(['project-blueprint', variables.projectId], (oldBlueprintData: any) => {
        if (oldBlueprintData?.data) {
          const updatedBlueprintData = {
            ...oldBlueprintData,
            data: {
              ...oldBlueprintData.data,
              updatedAt: data.updatedAt || new Date().toISOString(),
              // Update nested blueprint structure to match STEP-7 expectations
              blueprintData: {
                ...oldBlueprintData.data.blueprintData,
                updatedAt: data.updatedAt || new Date().toISOString(),
                blueprint: {
                  ...oldBlueprintData.data.blueprintData?.blueprint,
                  bookMetadata: data.bookMetadata
                }
              }
            }
          };
          console.log('🔄 Updated blueprint React Query cache for STEP-7:', {
            projectId: variables.projectId,
            oldTimestamp: oldBlueprintData.data?.blueprintData?.updatedAt,
            newTimestamp: data.updatedAt,
            updated: true
          });
          return updatedBlueprintData;
        }
        return oldBlueprintData;
      });

      // CRITICAL FIX: Invalidate blueprint React Query cache to force fresh data fetch from server
      // This ensures STEP-7 hydration gets the latest data after save
      queryClient.invalidateQueries({ queryKey: ['project-blueprint', variables.projectId] });

      // CRITICAL FIX: Also invalidate the project data to ensure characters tab refresh
      queryClient.invalidateQueries({ queryKey: ['story-project-summary', variables.projectId] });

      // CRITICAL: Update local project state with fresh data from successful save
      if (variables.onStateUpdate) {
        variables.onStateUpdate(data);
      }

      // 🚨 ISSUE FIX: Don't invalidate immediately after updating cache
      // This was causing refetch that overwrote our fresh cache data
      // invalidateProjectAndBlueprint(variables.projectId);

      // Instead, only invalidate dashboard summaries list to refresh other views
      queryClient.invalidateQueries({ queryKey: ["/api/story-projects/summaries"] });

      toast({
        title: "Book Details Saved",
        description: "Your book metadata has been updated successfully.",
      });
    },
    onError: (error: any, variables) => {
      console.error('💥 MUTATION ERROR HANDLER:', error, variables);
      
      // Reset saving state on error
      if (variables.setBlueprintSaving) {
        variables.setBlueprintSaving(false);
      }
      
      toast({
        title: "Save Failed",
        description: error.message || "Failed to save book metadata.",
        variant: "destructive",
      });
    },
  });

  // Save world building data
  const saveWorldBuilding = useMutation({
    mutationFn: async ({ projectId, worldBuildingData, setBlueprintSaving }: {
      projectId: string;
      worldBuildingData: any;
      setBlueprintSaving?: (saving: boolean) => void;
    }) => {
      // Set saving state immediately when save starts
      if (setBlueprintSaving) {
        setBlueprintSaving(true);
      }
      
      const response = await apiRequest(`/api/story-projects/${projectId}/world-building`, 'PUT', { worldBuildingData });
      return response.json();
    },
    onSuccess: (data, variables) => {
      // Reset saving state immediately after successful save
      if (variables.setBlueprintSaving) {
        variables.setBlueprintSaving(false);
      }
      
      // CRITICAL: Update React Query cache directly with fresh API data
      queryClient.setQueryData(['story-project-summary', variables.projectId], (oldData: any) => {
        console.log('🔄 Updating React Query cache with fresh world building API response:', {
          oldWorldBuilding: oldData?.worldBuildingData ? Object.keys(oldData.worldBuildingData) : null,
          newWorldBuilding: data.worldBuildingData ? Object.keys(data.worldBuildingData) : null,
          fullApiResponse: data
        });
        return {
          ...oldData,
          ...data, // Merge all fresh API data
          updatedAt: data.updatedAt || new Date().toISOString()
        };
      });
      
      // CRITICAL: Update localStorage cache with synchronized update (all 3 cache objects)
      cacheManager.updateAllProjectCaches(variables.projectId, data, 'world-building');
      
      // Only invalidate dashboard summaries list to refresh other views
      queryClient.invalidateQueries({ queryKey: ["/api/story-projects/summaries"] });

      toast({
        title: "World Building Saved",
        description: "Your world building data has been updated successfully.",
      });
    },
    onError: (error: any, variables) => {
      // Reset saving state on error
      if (variables.setBlueprintSaving) {
        variables.setBlueprintSaving(false);
      }
      
      toast({
        title: "Save Failed",
        description: error.message || "Failed to save world building data.",
        variant: "destructive",
      });
    },
  });

  // Save author details
  const saveAuthorDetails = useMutation({
    mutationFn: async ({ projectId, authorDetails, setBlueprintSaving }: {
      projectId: string;
      authorDetails: any;
      setBlueprintSaving?: (saving: boolean) => void;
    }) => {
      console.log('💾 [BOOK-STRUCTURE-STUDIO] Starting Author Details save:', {
        projectId,
        hasAuthorDetails: !!authorDetails,
        authorDetailsKeys: authorDetails ? Object.keys(authorDetails) : null,
        authorName: authorDetails?.authorName || 'Not set',
        timestamp: new Date().toISOString()
      });
      
      // Set saving state immediately when save starts
      if (setBlueprintSaving) {
        setBlueprintSaving(true);
      }
      
      const response = await apiRequest(`/api/story-projects/${projectId}/author-details`, 'PUT', { authorDetails });
      return response.json();
    },
    onSuccess: async (data, variables) => {
      // Reset saving state immediately after successful save
      if (variables.setBlueprintSaving) {
        variables.setBlueprintSaving(false);
      }
      
      // CRITICAL: Update React Query cache directly with fresh API data
      queryClient.setQueryData(['story-project-summary', variables.projectId], (oldData: any) => {
        console.log('🔄 [BOOK-STRUCTURE-STUDIO] Updating React Query cache with fresh author details API response:', {
          projectId: variables.projectId,
          oldAuthorDetails: oldData?.authorDetails,
          newAuthorDetails: data.authorDetails,
          authorName: data.authorDetails?.authorName || 'Not set',
          cacheAction: 'UPDATE story-project-summary',
          fullApiResponse: data
        });
        return {
          ...oldData,
          ...data, // Merge all fresh API data
          updatedAt: data.updatedAt || new Date().toISOString()
        };
      });
      
      // CRITICAL: Update localStorage cache with synchronized update (all 3 cache objects)
      cacheManager.updateAllProjectCaches(variables.projectId, data, 'author-details');
      
      console.log('💾 [BOOK-STRUCTURE-STUDIO] Updated localStorage cache with author details:', {
        projectId: variables.projectId,
        authorName: data.authorDetails?.authorName || 'Not set',
        cacheAction: 'UPDATE localStorage (all 3 caches)',
        context: 'author-details'
      });
      
      // AUTO-SYNC: Update back matter with consolidated author information
      try {
        const currentProjectData = queryClient.getQueryData(['story-project-summary', variables.projectId]) as any;
        const currentBackMatter = currentProjectData?.blueprint?.backMatter;
        
        if (currentBackMatter && data.authorDetails) {
          
          // Consolidate back matter with new author details
          const consolidatedBackMatter = consolidateBackMatterAuthorInfo(
            currentBackMatter,
            data.authorDetails
          );
          
          // Save the consolidated back matter automatically
          try {
            const backMatterResponse = await apiRequest(
              `/api/story-projects/${variables.projectId}/back-matter`,
              'PUT',
              { backMatter: consolidatedBackMatter }
            );
            
            const backMatterData = await backMatterResponse.json();
            
            // Update cache with consolidated back matter
            queryClient.setQueryData(['story-project-summary', variables.projectId], (oldData: any) => {
              return {
                ...oldData,
                ...backMatterData,
                updatedAt: backMatterData.updatedAt || new Date().toISOString()
              };
            });
            
            // Update localStorage cache
// FIX: Argument of type '"back-matter-auto-sync"' is not assignable to parameter of type '"idea-hub" | "book-metadata" | "world-building" | "author-details" | "front-matter" | "back-matter" | "publishing-essentials" | "chapters-outline"'.
            cacheManager.updateAllProjectCaches(variables.projectId, backMatterData, 'back-matter');
            
          } catch (backMatterError) {
            console.warn('⚠️ Auto-sync: Failed to update back matter:', backMatterError);
            // Don't throw error - this is a background operation
          }
        }
      } catch (autoSyncError) {
        console.warn('⚠️ Auto-sync: Error during back matter consolidation:', autoSyncError);
        // Don't throw error - main save was successful
      }
      
      // Only invalidate dashboard summaries list to refresh other views
      queryClient.invalidateQueries({ queryKey: ["/api/story-projects/summaries"] });
      
      // CRITICAL FIX: Also invalidate main project cache to ensure Author Details appear on tab revisit
      queryClient.invalidateQueries({ queryKey: ['story-project', variables.projectId] });
      
      console.log('🔄 [BOOK-STRUCTURE-STUDIO] Cache invalidation complete:', {
        projectId: variables.projectId,
        invalidatedCaches: ['story-project-summary', 'story-project', '/api/story-projects/summaries'],
        authorName: data.authorDetails?.authorName || 'Not set',
        nextAction: 'Fresh data will be loaded on next request'
      });

      toast({
        title: "Author Details Saved",
        description: "Your author details and back matter have been updated successfully.",
      });
    },
    onError: (error: any, variables) => {
      // Reset saving state on error
      if (variables.setBlueprintSaving) {
        variables.setBlueprintSaving(false);
      }
      
      toast({
        title: "Save Failed",
        description: error.message || "Failed to save author details.",
        variant: "destructive",
      });
    },
  });

  // Save front matter
  const saveFrontMatter = useMutation({
    mutationFn: async ({ projectId, frontMatter, setBlueprintSaving }: {
      projectId: string;
      frontMatter: any;
      setBlueprintSaving?: (saving: boolean) => void;
    }) => {
      if (setBlueprintSaving) setBlueprintSaving(true);
      const response = await apiRequest(`/api/story-projects/${projectId}/front-matter`, 'PUT', { frontMatter });
      return response.json();
    },
    onSuccess: (data, variables) => {
      if (variables.setBlueprintSaving) variables.setBlueprintSaving(false);
      
      // CRITICAL: Update React Query cache directly with fresh API data
      queryClient.setQueryData(['story-project-summary', variables.projectId], (oldData: any) => {
        console.log('🔄 Updating React Query cache with fresh front matter API response:', {
          oldFrontMatter: oldData?.frontMatter,
          newFrontMatter: data.frontMatter,
          fullApiResponse: data
        });
        return {
          ...oldData,
          ...data, // Merge all fresh API data
          updatedAt: data.updatedAt || new Date().toISOString()
        };
      });
      
      // CRITICAL: Update localStorage cache with synchronized update (all 3 cache objects)
      cacheManager.updateAllProjectCaches(variables.projectId, data, 'front-matter');
      
      // Only invalidate dashboard summaries list to refresh other views
      queryClient.invalidateQueries({ queryKey: ["/api/story-projects/summaries"] });
      toast({
        title: "Front Matter Saved",
        description: "Your front matter has been updated successfully.",
      });
    },
    onError: (error: any, variables) => {
      if (variables.setBlueprintSaving) variables.setBlueprintSaving(false);
      toast({
        title: "Save Failed",
        description: error.message || "Failed to save front matter.",
        variant: "destructive",
      });
    },
  });

  // Save back matter
  const saveBackMatter = useMutation({
    mutationFn: async ({ projectId, backMatter, setBlueprintSaving }: {
      projectId: string;
      backMatter: any;
      setBlueprintSaving?: (saving: boolean) => void;
    }) => {
      if (setBlueprintSaving) setBlueprintSaving(true);
      const response = await apiRequest(`/api/story-projects/${projectId}/back-matter`, 'PUT', { backMatter });
      return response.json();
    },
    onSuccess: (data, variables) => {
      if (variables.setBlueprintSaving) variables.setBlueprintSaving(false);
      
      // CRITICAL: Update React Query cache directly with fresh API data
      queryClient.setQueryData(['story-project-summary', variables.projectId], (oldData: any) => {
        console.log('🔄 Updating React Query cache with fresh back matter API response:', {
          oldBackMatter: oldData?.backMatter,
          newBackMatter: data.backMatter,
          fullApiResponse: data
        });
        return {
          ...oldData,
          ...data, // Merge all fresh API data
          updatedAt: data.updatedAt || new Date().toISOString()
        };
      });
      
      // CRITICAL: Update localStorage cache with synchronized update (all 3 cache objects)
      cacheManager.updateAllProjectCaches(variables.projectId, data, 'back-matter');
      
      // Only invalidate dashboard summaries list to refresh other views
      queryClient.invalidateQueries({ queryKey: ["/api/story-projects/summaries"] });
      toast({
        title: "Back Matter Saved",
        description: "Your back matter has been updated successfully.",
      });
    },
    onError: (error: any, variables) => {
      if (variables.setBlueprintSaving) variables.setBlueprintSaving(false);
      toast({
        title: "Save Failed",
        description: error.message || "Failed to save back matter.",
        variant: "destructive",
      });
    },
  });

  // Save publishing essentials
  const savePublishingEssentials = useMutation({
    mutationFn: async ({ projectId, publishingEssentials, setBlueprintSaving }: {
      projectId: string;
      publishingEssentials: any;
      setBlueprintSaving?: (saving: boolean) => void;
    }) => {
      if (setBlueprintSaving) setBlueprintSaving(true);
      const response = await apiRequest(`/api/story-projects/${projectId}/publishing-essentials`, 'PUT', { publishingEssentials });
      return response.json();
    },
    onSuccess: (data, variables) => {
      if (variables.setBlueprintSaving) variables.setBlueprintSaving(false);
      
      // CRITICAL: Update React Query cache directly with fresh API data
      queryClient.setQueryData(['story-project-summary', variables.projectId], (oldData: any) => {
        console.log('🔄 Updating React Query cache with fresh publishing essentials API response:', {
          oldPublishingEssentials: oldData?.publishingEssentials,
          newPublishingEssentials: data.publishingEssentials,
          fullApiResponse: data
        });
        return {
          ...oldData,
          ...data, // Merge all fresh API data
          updatedAt: data.updatedAt || new Date().toISOString()
        };
      });
      
      // CRITICAL: Update localStorage cache with synchronized update (all 3 cache objects)
      cacheManager.updateAllProjectCaches(variables.projectId, data, 'publishing-essentials');
      
      // Only invalidate dashboard summaries list to refresh other views
      queryClient.invalidateQueries({ queryKey: ["/api/story-projects/summaries"] });
      toast({
        title: "Publishing Essentials Saved",
        description: "Your publishing essentials have been updated successfully.",
      });
    },
    onError: (error: any, variables) => {
      if (variables.setBlueprintSaving) variables.setBlueprintSaving(false);
      toast({
        title: "Save Failed",
        description: error.message || "Failed to save publishing essentials.",
        variant: "destructive",
      });
    },
  });

  return {
    saveIdeaHub,
    saveBookMetadata,
    saveWorldBuilding,
    saveAuthorDetails,
    saveFrontMatter,
    saveBackMatter,
    savePublishingEssentials,
  };
};